export const PRACTICE_SUBJECTS = [
  {
    key: 'algebra',
    label: 'Algebra',
    description: 'Equations, expressions, and pattern-based reasoning.',
    skills: [
      {
        skillCode: 'ALG-2-1-3',
        focusName: 'Simplifying expressions',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
        reason: 'You were close on collecting like terms cleanly in the last set.',
        recency: 'Last practised yesterday',
        starterReason: 'A steady algebra anchor if you want to begin with something familiar and structured.',
      },
      {
        skillCode: 'ALG-2-2-5',
        focusName: 'Simultaneous equations',
        status: 'NEEDS_REVIEW',
        recommendationStatus: 'Weak',
        reason: 'Recent slips suggest elimination still needs a steadier method.',
        recency: 'Last practised 4 days ago',
      },
      {
        skillCode: 'ALG-2-3',
        focusName: 'Sequences',
        status: 'STRONG',
        recommendationStatus: null,
        reason: 'Recently secure, but still available if you want a confident warm-up.',
        recency: 'Last practised 2 days ago',
      },
    ],
  },
  {
    key: 'number',
    label: 'Number',
    description: 'Fluency with number facts, ratio, and standard form.',
    skills: [
      {
        skillCode: 'NUM-1-1',
        focusName: 'Number and rounding',
        status: 'STRONG',
        recommendationStatus: null,
        reason: 'This has looked secure in recent practice.',
        recency: 'Last practised today',
        starterReason: 'A confidence-building place to start and a useful baseline for future recommendations.',
      },
      {
        skillCode: 'NUM-1-3',
        focusName: 'Index laws and standard form',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
        reason: 'You are moving forward, but powers with negatives still need one more pass.',
        recency: 'Last practised 2 days ago',
      },
      {
        skillCode: 'NUM-1-4',
        focusName: 'Ratio and proportion',
        status: 'NEEDS_REVIEW',
        recommendationStatus: 'Needs review',
        reason: 'This is due a revisit before it starts to drift.',
        recency: 'Last practised 6 days ago',
        starterReason: 'A strong core topic if you want to start with practical number relationships.',
      },
    ],
  },
  {
    key: 'geometry',
    label: 'Geometry',
    description: 'Shape properties, angle facts, and spatial problem solving.',
    skills: [
      {
        skillCode: 'GEO-3-1',
        focusName: 'Properties of shape',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
        reason: 'Classification is improving, but precision with vocabulary is still building.',
        recency: 'Last practised 3 days ago',
        starterReason: 'A broad geometry starting point that helps build the profile from clear, foundational questions.',
      },
      {
        skillCode: 'GEO-3-4',
        focusName: 'Angle facts',
        status: 'NEEDS_REVIEW',
        recommendationStatus: 'Needs review',
        reason: 'This has been quiet for a few days and is ready for a quick check-in.',
        recency: 'Last practised 5 days ago',
      },
      {
        skillCode: 'GEO-3-7',
        focusName: 'Pythagoras and trigonometry',
        status: 'STRONG',
        recommendationStatus: null,
        reason: 'Recent answers were accurate and efficient.',
        recency: 'Last practised yesterday',
      },
    ],
  },
  {
    key: 'statistics',
    label: 'Statistics',
    description: 'Data handling, interpretation, and probability.',
    skills: [
      {
        skillCode: 'STAT-4-1',
        focusName: 'Data collection methods',
        status: 'STRONG',
        recommendationStatus: null,
        reason: 'Secure recently, with sensible method choices.',
        recency: 'Last practised 2 days ago',
      },
      {
        skillCode: 'STAT-4-2',
        focusName: 'Representing and interpreting data',
        status: 'NEEDS_REVIEW',
        recommendationStatus: 'Weak',
        reason: 'Reading charts is mostly there, but interpretation still drops marks.',
        recency: 'Last practised 7 days ago',
      },
      {
        skillCode: 'STAT-4-3',
        focusName: 'Single-event probability',
        status: 'IN_PROGRESS',
        recommendationStatus: 'In progress',
        reason: 'You are getting the setup right, but notation still needs consistency.',
        recency: 'Last practised yesterday',
      },
    ],
  },
];

const PRACTICE_AUDIENCE_COPY = {
  student: {
    exactTopicDescription: 'Choose one exact topic or micro-skill when you want precise practice or targeted revision.',
  },
  tutor: {
    exactTopicDescription: 'Choose one exact topic or micro-skill when you want precise practice, tutor-directed focus, or targeted revision.',
  },
};

// The backend's `selection_modes` capability block carries machine reason
// codes, never prose. Display copy for each code lives here so the builder can
// never render a raw code, and so the two reasons stay materially different:
// PRODUCTION_DISABLED is "not switched on yet", NO_STUDENT_HISTORY is "there is
// nothing to review until some practice exists".
const SELECTION_MODE_UNAVAILABLE_COPY = {
  student: {
    NO_STUDENT_HISTORY: 'Available after you complete some practice.',
    PRODUCTION_DISABLED: 'Not available yet. This session type is still being rolled out.',
  },
  tutor: {
    NO_STUDENT_HISTORY: 'Available after the student completes some practice.',
    PRODUCTION_DISABLED: 'Not available yet. This session type is still being rolled out.',
  },
};

// Covers an unrecognised code and the fail-safe case where the backend sent no
// capability row at all for the mode.
const SELECTION_MODE_UNAVAILABLE_FALLBACK_COPY = 'Not available right now.';

export function getPracticeAudienceCopy(role) {
  return role === 'STUDENT' ? PRACTICE_AUDIENCE_COPY.student : PRACTICE_AUDIENCE_COPY.tutor;
}

export function getSelectionModeUnavailableCopy(role, reasonCode) {
  const audience = role === 'STUDENT' ? 'student' : 'tutor';
  const code = typeof reasonCode === 'string' ? reasonCode.trim() : '';
  return SELECTION_MODE_UNAVAILABLE_COPY[audience][code] || SELECTION_MODE_UNAVAILABLE_FALLBACK_COPY;
}

export function getPracticeRecommendations() {
  return PRACTICE_SUBJECTS
    .flatMap((subject) => subject.skills.map((skill) => ({ ...skill, subjectLabel: subject.label })))
    .filter((skill) => skill.recommendationStatus)
    .slice(0, 4);
}

export function getPracticeStarterSuggestions() {
  return PRACTICE_SUBJECTS
    .flatMap((subject) => subject.skills.map((skill) => ({ ...skill, subjectLabel: subject.label })))
    .filter((skill) => Boolean(skill.starterReason))
    .slice(0, 4);
}

export function getAllPracticeTopics() {
  return PRACTICE_SUBJECTS.map((subject) => ({
    ...subject,
    skills: subject.skills.map((skill) => ({
      ...skill,
      subjectLabel: subject.label,
    })),
  }));
}
